/**
 * Compliance Settings — Phase 4 of readiness-scoring.
 *
 * One row per framework in scope for this workspace: shows the current
 * accreditation target (if any) and lets an owner/admin set/clear one.
 *
 * The target date is a workspace decision, not editorial. Setting it
 * powers the countdown widget on the Dashboard framework score cards
 * and (in "strict" mode) will eventually gate the readiness score
 * after the date passes. In "soft" mode the score is unaffected — the
 * date is a milestone we surface for planning.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  Input,
  Textarea,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useRequiredProfile } from '../lib/auth';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  deleteComplianceTarget,
  getProfile,
  listComplianceTargets,
  upsertComplianceTarget,
  type AccreditationTarget,
} from '../lib/api';
import { useInvalidate } from '../lib/query-keys';

interface TargetDraft {
  framework_id: string;
  framework_name: string;
  existing: AccreditationTarget | null;
  target_date: string;
  mode: 'soft' | 'strict';
  notes: string;
}

export function ComplianceSettings() {
  const workspaceCtx = useOptionalWorkspace();
  const workspaceSlug = workspaceCtx?.workspace.slug ?? null;
  const canManage =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';
  const invalidate = useInvalidate();
  const profileId = useRequiredProfile();

  const profile = useWorkspaceQuery(
    (s) => queryKeys.wsProfile(s, profileId),
    () => getProfile(profileId),
  );
  const targetsAsync = useWorkspaceQuery(
    (s) => queryKeys.complianceTargets(s),
    (s) => listComplianceTargets(s),
  );

  const [drafts, setDrafts] = useState<Record<string, TargetDraft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const rows = useMemo(() => {
    const frameworks = profile.data?.applicable_frameworks ?? [];
    const byFw = new Map(
      (targetsAsync.data?.targets ?? []).map((t) => [t.framework_id, t])
    );
    return frameworks.map((fw) => ({
      framework_id: fw.framework_id,
      framework_name: fw.name,
      existing: byFw.get(fw.framework_id) ?? null,
    }));
  }, [profile.data, targetsAsync.data]);

  useEffect(() => {
    // Seed drafts from server data so cancel-then-edit returns to the
    // persisted target rather than a stale in-flight edit.
    const seeded: Record<string, TargetDraft> = {};
    for (const r of rows) {
      seeded[r.framework_id] = {
        framework_id: r.framework_id,
        framework_name: r.framework_name,
        existing: r.existing,
        target_date: r.existing?.target_date ?? '',
        mode: r.existing?.mode ?? 'soft',
        notes: r.existing?.notes ?? '',
      };
    }
    setDrafts(seeded);
  }, [rows]);

  const save = useCallback(
    async (frameworkId: string) => {
      if (!workspaceSlug) return;
      const draft = drafts[frameworkId];
      if (!draft) return;
      if (!draft.target_date) {
        setSaveError('Pick a target date first.');
        return;
      }
      setSavingId(frameworkId);
      setSaveError(null);
      try {
        await upsertComplianceTarget(workspaceSlug, frameworkId, {
          target_date: draft.target_date,
          mode: draft.mode,
          notes: draft.notes.trim() || null,
        });
        await targetsAsync.refetch();
        invalidate('compliance-target:changed', workspaceSlug);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to save target');
      } finally {
        setSavingId(null);
      }
    },
    [workspaceSlug, drafts, targetsAsync, invalidate]
  );

  const clear = useCallback(
    async (frameworkId: string) => {
      if (!workspaceSlug) return;
      setSavingId(frameworkId);
      setSaveError(null);
      try {
        await deleteComplianceTarget(workspaceSlug, frameworkId);
        await targetsAsync.refetch();
        invalidate('compliance-target:changed', workspaceSlug);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to clear target');
      } finally {
        setSavingId(null);
      }
    },
    [workspaceSlug, targetsAsync, invalidate]
  );

  if (!workspaceSlug) {
    return (
      <Alert variant="error">
        Compliance targets are workspace-scoped. Open this page from within a
        workspace.
      </Alert>
    );
  }

  if (profile.loading || targetsAsync.loading) {
    return <Spinner label="Loading compliance targets" />;
  }
  if (profile.error) return <Alert variant="error">{profile.error}</Alert>;
  if (targetsAsync.error) return <Alert variant="error">{targetsAsync.error}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)', maxWidth: '48rem' }}>
      <PageHeader
        title="Compliance targets"
        subtitle="Lock a target date per framework. Countdown appears on Dashboard scores."
      />

      {saveError ? <Alert variant="error">{saveError}</Alert> : null}

      {rows.length === 0 ? (
        <EmptyState
          title="No frameworks in scope yet"
          description="Complete your onboarding profile to see the frameworks you should be tracking."
          action={
            <Link to={`/w/${workspaceSlug}/onboarding?mode=edit`}>
              <Button variant="primary" compact>
                Edit profile
              </Button>
            </Link>
          }
        />
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {rows.map((r) => {
            const d = drafts[r.framework_id];
            if (!d) return null;
            const dirty =
              d.target_date !== (r.existing?.target_date ?? '') ||
              d.mode !== (r.existing?.mode ?? 'soft') ||
              d.notes !== (r.existing?.notes ?? '');
            return (
              <li
                key={r.framework_id}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  background: 'var(--surface)',
                  padding: 'var(--space-4)',
                  display: 'grid',
                  gap: 'var(--space-3)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'baseline' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
                      {r.framework_name}
                    </h3>
                    <p
                      style={{
                        margin: '2px 0 0',
                        color: 'var(--text-dim)',
                        fontSize: 'var(--font-size-3xs)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {r.framework_id}
                    </p>
                  </div>
                  {r.existing ? (
                    <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
                      Target set
                    </span>
                  ) : null}
                </div>

                <div
                  style={{
                    display: 'grid',
                    gap: 'var(--space-3)',
                    gridTemplateColumns: 'minmax(160px, 200px) minmax(140px, 160px) 1fr',
                    alignItems: 'end',
                  }}
                >
                  <Field id={`date-${r.framework_id}`} label="Target date">
                    <Input
                      id={`date-${r.framework_id}`}
                      type="date"
                      value={d.target_date}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [r.framework_id]: { ...d, target_date: e.target.value },
                        }))
                      }
                      disabled={!canManage || savingId === r.framework_id}
                    />
                  </Field>
                  <Field id={`mode-${r.framework_id}`} label="Mode">
                    <Select
                      id={`mode-${r.framework_id}`}
                      value={d.mode}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [r.framework_id]: {
                            ...d,
                            mode: e.target.value as 'soft' | 'strict',
                          },
                        }))
                      }
                      disabled={!canManage || savingId === r.framework_id}
                    >
                      <option value="soft">Soft — milestone only</option>
                      <option value="strict">Strict — gates score</option>
                    </Select>
                  </Field>
                  <Field id={`notes-${r.framework_id}`} label="Notes (optional)">
                    <Textarea
                      id={`notes-${r.framework_id}`}
                      rows={1}
                      value={d.notes}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [r.framework_id]: { ...d, notes: e.target.value },
                        }))
                      }
                      placeholder="e.g. FedRAMP High JAB path — 3PAO kick-off Q1"
                      disabled={!canManage || savingId === r.framework_id}
                    />
                  </Field>
                </div>

                {canManage ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                    {r.existing ? (
                      <Button
                        variant="secondary"
                        onClick={() => clear(r.framework_id)}
                        disabled={savingId === r.framework_id}
                      >
                        {savingId === r.framework_id ? 'Clearing…' : 'Clear target'}
                      </Button>
                    ) : null}
                    <Button
                      variant="primary"
                      onClick={() => save(r.framework_id)}
                      disabled={!dirty || savingId === r.framework_id || !d.target_date}
                    >
                      {savingId === r.framework_id ? 'Saving…' : 'Save target'}
                    </Button>
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)', margin: 0 }}>
                    Owner or admin required to change compliance targets.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
